/**
 * 🔌 SignalR Service - TAM MÜKƏMMƏL VERSİYA
 * 
 * Fayl yeri: src/components/services/SignalRService.js
 * 
 * Backend Hub-ları:
 * - /auctionHub → UpdateBid, AuctionEnded, AuctionExtended, UserOutbid, AuctionWon
 * - /notificationHub → ReceiveNotification, NotificationRead, BalanceUpdated
 * 
 * @version 5.0.0
 */

import * as signalR from '@microsoft/signalr';

const API_BASE_URL = 'https://api.herracim.az';

// Connection states
export const ConnectionState = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    RECONNECTING: 'reconnecting',
    ERROR: 'error'
};

// Event adları - TAM SİYAHI
export const SignalREvents = {
    // ═══════════════════════════════════════════════════════════════
    // 🎯 AUCTION HUB EVENTS
    // ═══════════════════════════════════════════════════════════════
    UPDATE_BID: 'UpdateBid',              // Yeni təklif gəldikdə
    USER_OUTBID: 'UserOutbid',            // İstifadəçinin təklifi keçildikdə
    AUCTION_ENDED: 'AuctionEnded',        // Hərrac bitdikdə
    AUCTION_EXTENDED: 'AuctionExtended',  // Hərrac uzadıldıqda
    AUCTION_WON: 'AuctionWon',            // Hərrac qazanıldıqda
    
    // ═══════════════════════════════════════════════════════════════
    // 🔔 NOTIFICATION HUB EVENTS
    // ═══════════════════════════════════════════════════════════════
    RECEIVE_NOTIFICATION: 'ReceiveNotification',  // Yeni bildiriş
    NOTIFICATION_READ: 'NotificationRead',        // Bildiriş oxundu
    NOTIFICATION_COUNT: 'NotificationCount',      // Bildiriş sayı yeniləndi
    BALANCE_UPDATED: 'BalanceUpdated',            // Balans yeniləndi
    PONG: 'Pong'                                  // Ping cavabı
};

class SignalRService {
    constructor() {
        this.auctionConnection = null;
        this.notificationConnection = null;
        this.connectionState = ConnectionState.DISCONNECTED;
        this.notificationConnectionState = ConnectionState.DISCONNECTED;
        
        // Event listeners
        this.eventListeners = new Map();
        
        // Retry configuration
        this.maxRetries = 5;
        this.retryCount = 0;
        
        // State change callbacks
        this.stateChangeCallbacks = [];
        
        // Aktiv auction groups
        this.activeAuctionGroups = new Set();
    }

    // ═══════════════════════════════════════════════════════════════
    // 🎯 AUCTION HUB - /auctionHub
    // ═══════════════════════════════════════════════════════════════

    async connectToAuctionHub() {
        if (this.auctionConnection?.state === signalR.HubConnectionState.Connected) {
            console.log('✅ AuctionHub: Artıq qoşuludur');
            return true;
        }

        try {
            this.setConnectionState(ConnectionState.CONNECTING);

            this.auctionConnection = new signalR.HubConnectionBuilder()
                .withUrl(`${API_BASE_URL}/auctionHub`, {
                    withCredentials: true
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext) => {
                        const delay = Math.min(Math.pow(2, retryContext.previousRetryCount) * 1000, 30000);
                        console.log(`🔄 AuctionHub: Reconnect ${retryContext.previousRetryCount + 1}, ${delay}ms sonra...`);
                        return delay;
                    }
                })
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            // Event listeners və connection handlers
            this.setupAuctionEventListeners();
            this.setupAuctionConnectionHandlers();

            await this.auctionConnection.start();
            
            this.setConnectionState(ConnectionState.CONNECTED);
            this.retryCount = 0;
            console.log('✅ AuctionHub: Qoşuldu');
            
            return true;
        } catch (error) {
            console.error('❌ AuctionHub xətası:', error.message);
            this.setConnectionState(ConnectionState.ERROR);
            
            if (this.retryCount < this.maxRetries) {
                this.retryCount++;
                console.log(`🔄 Yenidən cəhd (${this.retryCount}/${this.maxRetries})...`);
                await this.delay(2000 * this.retryCount);
                return this.connectToAuctionHub();
            }
            
            return false;
        }
    }

    setupAuctionConnectionHandlers() {
        this.auctionConnection.onreconnecting((error) => {
            console.log('🔄 AuctionHub: Yenidən qoşulur...', error?.message);
            this.setConnectionState(ConnectionState.RECONNECTING);
        });

        this.auctionConnection.onreconnected(async (connectionId) => {
            console.log('✅ AuctionHub: Yenidən qoşuldu');
            this.setConnectionState(ConnectionState.CONNECTED);
            this.retryCount = 0;
            
            // Əvvəlki otaqlara yenidən qoşul
            await this.rejoinAuctionGroups();
        });

        this.auctionConnection.onclose((error) => {
            console.log('🔌 AuctionHub: Bağlantı kəsildi', error?.message);
            this.setConnectionState(ConnectionState.DISCONNECTED);
        });
    }

    setupAuctionEventListeners() {
        // ═══════════════════════════════════════════════════════════
        // 📈 UpdateBid - Backend: SendAsync("UpdateBid", productId, userName, amount)
        // ═══════════════════════════════════════════════════════════
        this.auctionConnection.on('UpdateBid', (productId, userName, amount) => {
            console.log('📈 SignalR UpdateBid:', { productId, userName, amount });
            
            this.emit(SignalREvents.UPDATE_BID, {
                productId: productId,
                auctionId: productId,
                userName: userName,
                bidderName: userName,
                amount: amount,
                currentBid: amount,
                timestamp: new Date().toISOString()
            });
        });

        // ═══════════════════════════════════════════════════════════
        // 😤 UserOutbid - İstifadəçinin təklifi keçildikdə
        // ═══════════════════════════════════════════════════════════
        this.auctionConnection.on('UserOutbid', (data) => {
            console.log('😤 SignalR UserOutbid:', data);
            this.emit(SignalREvents.USER_OUTBID, {
                productId: data.productId || data.auctionId,
                auctionId: data.productId || data.auctionId,
                newBidAmount: data.newBidAmount || data.amount,
                newBidderName: data.newBidderName || data.userName,
                timestamp: data.timestamp || new Date().toISOString()
            });
        });

        // ═══════════════════════════════════════════════════════════
        // 🏁 AuctionEnded - Hərrac bitdikdə
        // ═══════════════════════════════════════════════════════════
        this.auctionConnection.on('AuctionEnded', (data) => {
            console.log('🏁 SignalR AuctionEnded:', data);
            this.emit(SignalREvents.AUCTION_ENDED, {
                productId: data.productId || data.auctionId,
                auctionId: data.productId || data.auctionId,
                winnerId: data.winnerId,
                winnerName: data.winnerName,
                finalBid: data.finalBid || data.winningBid,
                timestamp: data.timestamp || new Date().toISOString()
            });
        });

        // ═══════════════════════════════════════════════════════════
        // ⏰ AuctionExtended - Hərrac uzadıldıqda
        // ═══════════════════════════════════════════════════════════
        this.auctionConnection.on('AuctionExtended', (data) => {
            console.log('⏰ SignalR AuctionExtended:', data);
            this.emit(SignalREvents.AUCTION_EXTENDED, {
                productId: data.productId || data.auctionId,
                auctionId: data.productId || data.auctionId,
                newEndDate: data.newEndDate,
                extensionMinutes: data.extensionMinutes || 2,
                timestamp: data.timestamp || new Date().toISOString()
            });
        });

        // ═══════════════════════════════════════════════════════════
        // 🏆 AuctionWon - Hərrac qazanıldıqda
        // ═══════════════════════════════════════════════════════════
        this.auctionConnection.on('AuctionWon', (data) => {
            console.log('🏆 SignalR AuctionWon:', data);
            this.emit(SignalREvents.AUCTION_WON, {
                productId: data.productId || data.auctionId,
                auctionId: data.productId || data.auctionId,
                winnerId: data.winnerId || data.userId,
                winnerName: data.winnerName || data.userName,
                winningBid: data.winningBid || data.amount,
                productName: data.productName,
                timestamp: data.timestamp || new Date().toISOString()
            });
        });
    }

    // ═══════════════════════════════════════════════════════════════
    // 🎯 AUCTION GROUP MANAGEMENT
    // ═══════════════════════════════════════════════════════════════

    async joinAuction(auctionId) {
        if (!auctionId) return false;

        if (!this.isAuctionConnected()) {
            await this.connectToAuctionHub();
        }

        try {
            await this.auctionConnection.invoke('JoinAuction', String(auctionId));
            this.activeAuctionGroups.add(String(auctionId));
            console.log(`✅ Hərrac #${auctionId} otağına qoşuldu`);
            return true;
        } catch (error) {
            console.error(`❌ JoinAuction xətası:`, error.message);
            return false;
        }
    }

    async leaveAuction(auctionId) {
        if (!auctionId || !this.isAuctionConnected()) return false;

        try {
            await this.auctionConnection.invoke('LeaveAuction', String(auctionId));
            this.activeAuctionGroups.delete(String(auctionId));
            console.log(`👋 Hərrac #${auctionId} otağından ayrıldı`);
            return true;
        } catch (error) {
            console.error(`❌ LeaveAuction xətası:`, error.message);
            return false;
        }
    }

    async rejoinAuctionGroups() {
        for (const auctionId of this.activeAuctionGroups) {
            try {
                await this.auctionConnection.invoke('JoinAuction', auctionId);
                console.log(`🔄 Hərrac #${auctionId} otağına yenidən qoşuldu`);
            } catch (error) {
                console.error(`❌ Rejoin xətası #${auctionId}:`, error.message);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // 🔔 NOTIFICATION HUB - /notificationHub
    // ═══════════════════════════════════════════════════════════════

    async connectToNotificationHub() {
        if (this.notificationConnection?.state === signalR.HubConnectionState.Connected) {
            console.log('✅ NotificationHub: Artıq qoşuludur');
            return true;
        }

        try {
            this.notificationConnectionState = ConnectionState.CONNECTING;
            this.notifyStateChange();

            this.notificationConnection = new signalR.HubConnectionBuilder()
                .withUrl(`${API_BASE_URL}/notificationHub`, {
                    withCredentials: true
                })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            this.setupNotificationEventListeners();
            this.setupNotificationConnectionHandlers();

            await this.notificationConnection.start();
            
            this.notificationConnectionState = ConnectionState.CONNECTED;
            this.notifyStateChange();
            console.log('✅ NotificationHub: Qoşuldu');
            
            return true;
        } catch (error) {
            console.error('❌ NotificationHub xətası:', error.message);
            this.notificationConnectionState = ConnectionState.ERROR;
            this.notifyStateChange();
            return false;
        }
    }

    setupNotificationConnectionHandlers() {
        this.notificationConnection.onreconnecting(() => {
            console.log('🔄 NotificationHub: Yenidən qoşulur...');
            this.notificationConnectionState = ConnectionState.RECONNECTING;
            this.notifyStateChange();
        });

        this.notificationConnection.onreconnected(() => {
            console.log('✅ NotificationHub: Yenidən qoşuldu');
            this.notificationConnectionState = ConnectionState.CONNECTED;
            this.notifyStateChange();
        });

        this.notificationConnection.onclose(() => {
            console.log('🔌 NotificationHub: Bağlantı kəsildi');
            this.notificationConnectionState = ConnectionState.DISCONNECTED;
            this.notifyStateChange();
        });
    }

    setupNotificationEventListeners() {
        // ═══════════════════════════════════════════════════════════
        // 🔔 ReceiveNotification - Yeni bildiriş
        // ═══════════════════════════════════════════════════════════
        this.notificationConnection.on('ReceiveNotification', (notification) => {
            console.log('🔔 SignalR Notification:', notification);
            this.emit(SignalREvents.RECEIVE_NOTIFICATION, notification);
            
            // Notification tipinə görə əlavə eventlər emit et
            if (notification.type === 'bid_outbid') {
                this.emit(SignalREvents.USER_OUTBID, {
                    productId: notification.relatedAuctionId || notification.productId,
                    auctionId: notification.relatedAuctionId || notification.productId,
                    newBidAmount: notification.amount,
                    ...notification
                });
            } else if (notification.type === 'auction_won') {
                this.emit(SignalREvents.AUCTION_WON, {
                    productId: notification.relatedAuctionId || notification.productId,
                    auctionId: notification.relatedAuctionId || notification.productId,
                    winnerId: notification.userId,
                    ...notification
                });
            } else if (notification.type === 'auction_ended') {
                this.emit(SignalREvents.AUCTION_ENDED, {
                    productId: notification.relatedAuctionId || notification.productId,
                    auctionId: notification.relatedAuctionId || notification.productId,
                    ...notification
                });
            } else if (notification.type === 'deposit_refunded' || notification.type === 'deposit_confirmed') {
                this.emit(SignalREvents.BALANCE_UPDATED, {
                    userId: notification.userId,
                    type: notification.type,
                    amount: notification.amount,
                    ...notification
                });
            }
        });

        // ═══════════════════════════════════════════════════════════
        // ✓ NotificationRead - Bildiriş oxundu
        // ═══════════════════════════════════════════════════════════
        this.notificationConnection.on('NotificationRead', (notificationId) => {
            console.log('✓ Notification oxundu:', notificationId);
            this.emit(SignalREvents.NOTIFICATION_READ, notificationId);
        });

        // ═══════════════════════════════════════════════════════════
        // 🔢 NotificationCount - Bildiriş sayı yeniləndi
        // ═══════════════════════════════════════════════════════════
        this.notificationConnection.on('NotificationCount', (data) => {
            console.log('🔢 Notification Count:', data);
            this.emit(SignalREvents.NOTIFICATION_COUNT, {
                unreadCount: data.unreadCount || data
            });
        });

        // ═══════════════════════════════════════════════════════════
        // 💰 BalanceUpdated - Balans yeniləndi
        // ═══════════════════════════════════════════════════════════
        this.notificationConnection.on('BalanceUpdated', (data) => {
            console.log('💰 Balance Updated:', data);
            this.emit(SignalREvents.BALANCE_UPDATED, {
                userId: data.userId,
                balance: data.balance,
                change: data.change,
                reason: data.reason,
                timestamp: data.timestamp || new Date().toISOString()
            });
        });

        // ═══════════════════════════════════════════════════════════
        // 🏓 Pong - Ping cavabı
        // ═══════════════════════════════════════════════════════════
        this.notificationConnection.on('Pong', (timestamp) => {
            console.log('🏓 Pong:', timestamp);
            this.emit(SignalREvents.PONG, timestamp);
        });
    }

    // Bildirişi oxundu işarələ
    async markNotificationAsRead(notificationId) {
        if (this.notificationConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.notificationConnection.invoke('MarkAsRead', notificationId);
                return true;
            } catch (error) {
                console.error('❌ MarkAsRead xətası:', error.message);
                return false;
            }
        }
        return false;
    }

    // Ping göndər
    async ping() {
        if (this.notificationConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.notificationConnection.invoke('Ping');
                return true;
            } catch (error) {
                console.error('❌ Ping xətası:', error.message);
                return false;
            }
        }
        return false;
    }

    // ═══════════════════════════════════════════════════════════════
    // 📡 EVENT EMITTER
    // ═══════════════════════════════════════════════════════════════

    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event).add(callback);
        
        // Unsubscribe function qaytarır
        return () => this.off(event, callback);
    }

    off(event, callback) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).delete(callback);
        }
    }

    emit(event, data) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Event handler xətası (${event}):`, error);
                }
            });
        }
    }

    // Bütün listener-ları sil
    removeAllListeners(event) {
        if (event) {
            this.eventListeners.delete(event);
        } else {
            this.eventListeners.clear();
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // 🔧 STATE MANAGEMENT
    // ═══════════════════════════════════════════════════════════════

    setConnectionState(state) {
        this.connectionState = state;
        this.notifyStateChange();
    }

    notifyStateChange() {
        const state = {
            auctionState: this.connectionState,
            notificationState: this.notificationConnectionState,
            isAuctionConnected: this.isAuctionConnected(),
            isNotificationConnected: this.isNotificationConnected()
        };
        
        this.stateChangeCallbacks.forEach(cb => {
            try {
                cb(state);
            } catch (error) {
                console.error('State change callback xətası:', error);
            }
        });
    }

    onStateChange(callback) {
        this.stateChangeCallbacks.push(callback);
        return () => {
            const index = this.stateChangeCallbacks.indexOf(callback);
            if (index > -1) this.stateChangeCallbacks.splice(index, 1);
        };
    }

    isAuctionConnected() {
        return this.auctionConnection?.state === signalR.HubConnectionState.Connected;
    }

    isNotificationConnected() {
        return this.notificationConnection?.state === signalR.HubConnectionState.Connected;
    }

    // ═══════════════════════════════════════════════════════════════
    // 🧹 CLEANUP
    // ═══════════════════════════════════════════════════════════════

    async disconnect() {
        try {
            if (this.auctionConnection) {
                await this.auctionConnection.stop();
                this.auctionConnection = null;
            }
            
            if (this.notificationConnection) {
                await this.notificationConnection.stop();
                this.notificationConnection = null;
            }
            
            this.eventListeners.clear();
            this.activeAuctionGroups.clear();
            this.stateChangeCallbacks = [];
            this.connectionState = ConnectionState.DISCONNECTED;
            this.notificationConnectionState = ConnectionState.DISCONNECTED;
            
            console.log('🔌 SignalR: Bütün bağlantılar kəsildi');
        } catch (error) {
            console.error('❌ Disconnect xətası:', error);
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // 🛠️ UTILITY
    // ═══════════════════════════════════════════════════════════════

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    getStatus() {
        return {
            auctionHub: {
                state: this.connectionState,
                connected: this.isAuctionConnected(),
                activeGroups: Array.from(this.activeAuctionGroups)
            },
            notificationHub: {
                state: this.notificationConnectionState,
                connected: this.isNotificationConnected()
            },
            eventListeners: {
                total: this.eventListeners.size,
                events: Array.from(this.eventListeners.keys())
            }
        };
    }
}

// Singleton instance
const signalRService = new SignalRService();

// Development debug
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.__signalR = signalRService;
    window.__signalRStatus = () => signalRService.getStatus();
}

export default signalRService;